import { api } from 'src/services/api';
import { getHeaders, getQueryString, appendFormData } from 'src/services/utils';
import { DataApiInputParams } from 'src/types/api';
import type { AppNotification } from 'src/content/Notifications/types';

export type NotificationListResponse = { data: AppNotification[]; lastFetchSize: number; nextCursor: string | null };
export type NotificationCountsResponse = { data: { count: number; unread_count: number }[] };

export const notificationsApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getNotification: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const url = `notifications/?${getQueryString(arg.params)}`;
        const result: any = await fetchWithBQ({ url, ...getHeaders(url) });
        if (result.error) return result;
        const raw = result.data;
        if (arg.params?.type === 'list' && Array.isArray(raw?.data)) {
          return { data: { ...raw, data: raw.data as AppNotification[], lastFetchSize: raw.data.length, nextCursor: raw.next_cursor ?? null } };
        }
        return { data: raw };
      },
      serializeQueryArgs: ({ queryArgs }) => ({ type: queryArgs.params?.type, is_read: queryArgs.params?.is_read }),
      merge: (currentCache, newData, { arg }) => {
        const incoming = newData as any;
        if (arg.params?.type !== 'list' || !Array.isArray(incoming?.data)) {
          Object.assign(currentCache as any, newData);
          return;
        }
        if (!arg.params?.cursor) {
          (currentCache as any).data = incoming.data;
        } else {
          const ids = new Set((currentCache as any).data?.map((n: any) => n.id) ?? []);
          const newItems = incoming.data.filter((n: any) => !ids.has(n.id));
          (currentCache as any).data = [
            ...(currentCache as any).data,
            ...newItems
          ];
        }
        (currentCache as any).lastFetchSize = incoming.lastFetchSize;
        (currentCache as any).nextCursor = incoming.nextCursor;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.params?.cursor !== previousArg?.params?.cursor,
      providesTags: ['Notifications']
    }),
    updateNotification: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params ?? {});
        const url = `notifications/`;
        return fetchWithBQ({ url, method: 'PATCH', body: formData, ...getHeaders(url, formData) });
      },
      async onQueryStarted(arg: DataApiInputParams, { dispatch, queryFulfilled }) {
        const action = arg.params?.action;
        const patches: ReturnType<typeof api.util.updateQueryData>[] = [];

        if (action === 'read_status') {
          const patch = dispatch(
            api.util.updateQueryData('getNotification', { params: { type: 'list' } } as any, (draft: any) => {
              const item = draft.data?.find((n: any) => n.id === arg.params?.notification_id);
              if (item) item.is_read = arg.params?.is_read;
            })
          );
          patches.push(patch);
          const countPatch = dispatch(
            api.util.updateQueryData('getNotification', { params: { type: 'counts' } } as any, (draft: any) => {
              if (draft.data?.[0]) {
                draft.data[0].unread_count = Math.max(0, (draft.data[0].unread_count ?? 0) + (arg.params?.is_read ? -1 : 1));
              }
            })
          );
          patches.push(countPatch);
        } else if (action === 'mark_all_read') {
          const patch = dispatch(
            api.util.updateQueryData('getNotification', { params: { type: 'list' } } as any, (draft: any) => {
              draft.data?.forEach((n: any) => { n.is_read = true; });
            })
          );
          patches.push(patch);
          const countPatch = dispatch(
            api.util.updateQueryData('getNotification', { params: { type: 'counts' } } as any, (draft: any) => {
              if (draft.data?.[0]) draft.data[0].unread_count = 0;
            })
          );
          patches.push(countPatch);
        } else if (action === 'dismiss') {
          const patch = dispatch(
            api.util.updateQueryData('getNotification', { params: { type: 'list' } } as any, (draft: any) => {
              const idx = draft.data?.findIndex((n: any) => n.id === arg.params?.notification_id);
              if (idx !== undefined && idx !== -1) draft.data.splice(idx, 1);
            })
          );
          patches.push(patch);
        } else if (action === 'dismiss_all') {
          const patch = dispatch(
            api.util.updateQueryData('getNotification', { params: { type: 'list' } } as any, (draft: any) => {
              draft.data = [];
            })
          );
          patches.push(patch);
          const countPatch = dispatch(
            api.util.updateQueryData('getNotification', { params: { type: 'counts' } } as any, (draft: any) => {
              if (draft.data?.[0]) { draft.data[0].count = 0; draft.data[0].unread_count = 0; }
            })
          );
          patches.push(countPatch);
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: ['Notifications']
    }),
    getNotificationPreferences: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const url = `notifications-preferences/?${getQueryString(arg?.params)}`;
        const result: any = await fetchWithBQ({ url, ...getHeaders(url) });
        if (result.error) return result;
        return { data: result.data?.ret_val ?? result.data?.data ?? [] };
      },
      providesTags: ['NotificationPreferences']
    }),
    updateNotificationPreferences: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params ?? {});
        const url = `notifications-preferences/`;
        return fetchWithBQ({ url, method: 'PATCH', body: formData, ...getHeaders(url, formData) });
      },
      async onQueryStarted(arg: DataApiInputParams, { dispatch, queryFulfilled }) {
        const { action, category_id, is_enabled, email_enabled } = arg.params ?? {};
        const patch = dispatch(
          api.util.updateQueryData('getNotificationPreferences', { params: {} } as any, (draft: any) => {
            const item = draft?.find((p: any) => p.category_id === category_id);
            if (!item) return;
            if (action === 'enable_notification') item.is_enabled = is_enabled;
            if (action === 'enable_email') item.email_enabled = email_enabled;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
    getOrganizationNotificationPreferences: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const url = `organization-notifications-preferences/?${getQueryString(arg?.params)}`;
        const result: any = await fetchWithBQ({ url, ...getHeaders(url) });
        if (result.error) return result;
        return { data: result.data?.ret_val ?? result.data?.data ?? [] };
      },
      providesTags: ['OrganizationNotificationPreferences']
    }),
    updateOrganizationNotificationPreferences: builder.mutation({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const formData = appendFormData(arg.params ?? {});
        const url = `organization-notifications-preferences/`;
        return fetchWithBQ({ url, method: 'PATCH', body: formData, ...getHeaders(url, formData) });
      },
      async onQueryStarted(arg: DataApiInputParams, { dispatch, queryFulfilled }) {
        const { action, category_id, is_enabled, email_enabled, is_mandatory } = arg.params ?? {};
        const patch = dispatch(
          api.util.updateQueryData('getOrganizationNotificationPreferences', { params: {} } as any, (draft: any) => {
            const item = draft?.find((p: any) => p.category_id === category_id);
            if (!item) return;
            if (action === 'update_is_enabled') item.is_enabled = is_enabled;
            if (action === 'update_email_enabled') item.email_enabled = email_enabled;
            if (action === 'update_mandatory') item.is_mandatory = is_mandatory;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['NotificationPreferences']
    })
  })
});

export const {
  useGetNotificationQuery,
  useLazyGetNotificationQuery,
  useUpdateNotificationMutation,
  useGetNotificationPreferencesQuery,
  useLazyGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useGetOrganizationNotificationPreferencesQuery,
  useLazyGetOrganizationNotificationPreferencesQuery,
  useUpdateOrganizationNotificationPreferencesMutation
} = notificationsApi;
